<script>
import UserAvatar from '@components/user/user-avatar.vue'
import MessageInput from '@components/message/message-input.vue'
import MessageChatBubble from '@components/message/message-chat-bubble.vue'
import TextDivider from '@components/general/text-divider.vue'
import { mapActions, mapGetters } from 'vuex'
import baseMixin from '@mixins/baseMixin.js'
import { last, findLast } from 'lodash'
import messageMixin from '@/mixins/messageMixin'
import MessageContainerSkeleton from '../loaders/message-container-skeleton.vue'

export default {
  name: 'MessageContainer',
  components: {
    UserAvatar,
    MessageInput,
    MessageChatBubble,
    TextDivider,
    MessageContainerSkeleton,
  },
  mixins: [baseMixin, messageMixin],
  props: {
    contactId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      limit: 50, // number of messages to render initially
    }
  },
  computed: {
    ...mapGetters({
      isLoadingCandidateMessages: 'message/isLoadingCandidateMessages',
      getCandidateMessages: 'message/getCandidateMessages',
      hasMoreCandidateMessages: 'message/hasMoreCandidateMessages',
    }),
    contactMessages() {
      return this.getCandidateMessages(this.contactId, this.limit)
    },
    hasContact() {
      return !!this.contactMessages?.contact
    },
    messages() {
      return this.contactMessages?.messages || []
    },
    contactName() {
      return this.hasContact ? this.contactMessages.contact.name : ''
    },
    contactImage() {
      return this.hasContact ? this.contactMessages.contact.img : ''
    },
  },
  watch: {
    contactMessages: {
      deep: true,
      async handler() {
        // Scrolls to bottom of message container on any change to the messages
        await this.$nextTick
        this.scrollDown()
      },
    },
  },
  async created() {
    // Load messages
    this.getCandidateMessagesById(this.contactId)

    await this.$nextTick
    this.scrollDown()
  },
  methods: {
    ...mapActions({
      getCandidateMessagesById: 'message/getCandidateMessagesById',
    }),
    scrollDown() {
      const msgs = document.querySelectorAll('.message-group')
      const container = document.getElementById('message-container')

      // Prevent exception if no messages have been sent
      if (!msgs || msgs.length === 0) return

      let offsetHeight = 0

      // Calculate accumulated offset height for all message blocks
      for (const element of msgs) {
        offsetHeight += element.offsetHeight
      }

      container.scrollTop = Math.floor(offsetHeight)
    },
    increaseLimit() {
      this.limit += this.limit
    },
    lastReadMessage(messageId, groupKey) {
      const lastGroupKey = last(Object.keys(this.contactMessages.messages))

      if (lastGroupKey !== groupKey) return false

      const lastReadMessage = findLast(
        this.contactMessages.messages[lastGroupKey],
        (msg) => msg.readBy
      )

      if (!lastReadMessage) return false

      return lastReadMessage.id === messageId
    },
  },
}
</script>

<template>
  <MessageContainerSkeleton v-if="isLoadingCandidateMessages" />
  <v-card v-else class="fill-height d-flex flex-column" elevation="0">
    <div class="fill-height flex-grow">
      <v-toolbar
        v-if="messagesShowCandidateDetailsToolbar"
        :dense="isMobileViewPort"
        flat
      >
        <v-toolbar-title class="d-flex align-center">
          <UserAvatar
            :name="contactName"
            :image-url="contactImage"
            :size="isMobileViewPort ? 'sm' : 'md'"
          />
          <h2
            :class="[
              isMobileViewPort ? 'subtitle-2' : 'subtitle-1',
              'font-weight-medium ml-4',
            ]"
            >{{ contactName }}</h2
          >
        </v-toolbar-title>
      </v-toolbar>
      <v-divider v-if="messagesShowCandidateDetailsToolbar"></v-divider>
      <v-card-text
        id="message-container"
        ref="messageContainer"
        :style="messagesContainerScrollHeight"
        class="fill-height flex-grow-1 overflow-y-auto"
      >
        <div
          v-if="hasMoreCandidateMessages(contactId, limit)"
          class="text-center"
        >
          <a class="caption" @click="increaseLimit">Load More Messages...</a>
        </div>
        <template v-for="(items, key, index) in messages">
          <div :key="index" class="message-group pa-4">
            <TextDivider class="my-6" :text="key" />

            <template v-for="(item, ind) in items">
              <div :key="ind" :class="{ 'd-flex flex-row-reverse': item.me }">
                <MessageChatBubble
                  :message="item"
                  :last="lastReadMessage(item.id, key)"
                />
              </div>
            </template>
          </div>
        </template>
        <div id="scrolled-content" style="height: 20px" />
      </v-card-text>
    </div>
    <v-divider></v-divider>
    <v-card flat class="flex-shrink">
      <MessageInput :recipient-id="contactId" />
    </v-card>
  </v-card>
</template>
